// src/api/aiChat.js
// 中文简体注释
import { baseURL } from '@/utils/request' // 导入 baseURL

// ★ 新增：一个统一的函数来获取AI客服的所有初始化配置
/**
 * @description 获取AI客服的初始化配置 (包括欢迎语、二维码、推荐问题等)
 * @returns {Promise<Object>} 返回一个包含所有配置的对象
 */
export async function getAIChatConfig() {
  try {
    // 此接口是公开的，无需认证
    const response = await fetch(`${baseURL}/ai-chat/config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`服务器错误: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('获取AI客服配置失败:', error);
    // 发生错误时返回一个包含默认值的空对象，防止前端组件崩溃
    return {
        ai_welcome_text: '您好，欢迎使用AI客服！',
        ai_qr_code_url: null,
        ai_qr_code_text: null,
        ai_suggested_questions: []
    };
  }
}


/**
 * @description 调用后端AI聊天流式接口
 * @param {string} query 用户的提问
 * @param {function} onData 接收到数据流块时的回调函数
 * @param {function} onEnd 流结束时的回调函数
 * @param {function} onError 发生错误时的回调函数
 */
export async function getAIChatStream(query, onData, onEnd, onError) {
  try {
    // 尝试从 localStorage 获取 token（与 Vuex 持久化保持一致）
    let token = null;
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const userState = JSON.parse(userData);
        // Vuex 持久化的结构是 { user: { token, userInfo } }
        if (userState && userState.user && userState.user.token) {
          token = userState.user.token;
        }
      } catch (e) {
        console.warn('解析用户数据失败:', e);
      }
    }

    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // 使用 Fetch API 进行流式请求
    const response = await fetch(`${baseURL}/ai-chat/stream`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`服务器错误: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (onEnd) onEnd();
        break;
      }
      
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n\n').filter(line => line.trim() !== '');

      for (const line of lines) {
        if (line.trim() === 'event: end') {
          if (onEnd) onEnd();
          return;
        }
        if (line.includes('event: error')) {
           const dataPart = line.substring(line.indexOf('data: ') + 'data: '.length);
           try {
             const errorData = JSON.parse(dataPart);
             if (onError) onError(errorData.error || '未知错误');
           } catch(e) {
             if (onError) onError('解析错误信息失败');
           }
           return;
        }
        if (line.startsWith('data:')) {
          const dataPart = line.substring('data: '.length);
          try {
            if (dataPart.startsWith('{')) {
                const jsonData = JSON.parse(dataPart);
                if (onData && jsonData.content) {
                  onData(jsonData.content);
                }
            }
          } catch(e) {
             console.warn('无法解析的JSON数据块:', dataPart);
          }
        }
      }
    }
  } catch (error) {
    console.error('AI聊天请求失败:', error);
    if (onError) onError(error.message || '网络连接失败或服务器无响应');
  }
}